/**
 * Widget reorder helpers for plan detail: in-section swaps, cross-section moves, and the
 * destination list for the Move to Different Section modal.
 */

import {
  CASE_PLAN_WIDGET_DESTRUCTIVE_ACTIONS,
  CASE_PLAN_SECTION_STATUSES,
  CASE_PLAN_WIDGET_ARCHIVE_LABEL,
  CASE_PLAN_WIDGET_CONFIRMATION_COPY,
  CASE_PLAN_WIDGET_DELETE_LABEL,
  CASE_PLAN_WIDGET_FALLBACK_TITLE,
  CASE_PLAN_WIDGET_MOVE_TO_SECTION_LABEL,
  CASE_PLAN_WIDGET_STATUSES,
  PLAN_STATUS_WIDGET_DESTRUCTIVE_ACTION,
} from '../constants/reentry-planning.constants';
import { isPendingSection } from './case-plan-section.util';

// ========== Status predicates ==========

export const isActiveWidget = (widget) =>
  (widget?.status ?? CASE_PLAN_WIDGET_STATUSES.ACTIVE) === CASE_PLAN_WIDGET_STATUSES.ACTIVE;

export const isArchivedWidget = (widget) => widget?.status === CASE_PLAN_WIDGET_STATUSES.ARCHIVED;

export const isDeletedWidget = (widget) => widget?.status === CASE_PLAN_WIDGET_STATUSES.DELETED;

/** True while a widget exists only locally, waiting for its create to answer. */
export const isPendingWidget = (widget) => widget?.isPending === true;

const normalizePlanStatus = (planStatus) => String(planStatus ?? '').trim().toLowerCase();

/**
 * Destructive kebab action the plan status permits for widgets. Draft soft-deletes, Active and
 * Released archive, and every other status offers neither.
 *
 * @param {string} planStatus - `plan.status` from the detail response.
 * @returns {'delete' | 'archive' | null}
 */
export const resolveWidgetDestructiveAction = (planStatus) =>
  PLAN_STATUS_WIDGET_DESTRUCTIVE_ACTION[normalizePlanStatus(planStatus)] ?? null;

/** Every section render path uses this — Active tab, Archived tab, editable and read-only. */
export const filterActiveWidgets = (widgets = []) => widgets.filter(isActiveWidget);

/**
 * Removes kebab menu items attached during Active-tab editing. Archived and read-only renders
 * must not reuse them — a reload never carries menuOptions on mapped widgets.
 */
export const stripWidgetMenuOptions = (widget) => {
  if (widget?.menuOptions === undefined) {
    return widget;
  }

  const withoutMenu = { ...widget };
  delete withoutMenu.menuOptions;
  return withoutMenu;
};

/** Flat list of all archived widgets across all sections, preserving backend order within each section. */
export const collectOrphanedArchivedWidgets = (sections = []) =>
  sections.flatMap((section) =>
    (section.widgets ?? []).filter(isArchivedWidget).map(stripWidgetMenuOptions),
  );

const resolveWidgetTitleFromProps = (widget) => {
  if (typeof widget?.title === 'string' && widget.title.trim() !== '') {
    return widget.title.trim();
  }

  if (typeof widget?.props?.title === 'string' && widget.props.title.trim() !== '') {
    return widget.props.title.trim();
  }

  return '';
};

/** Display title for confirmation copy and standalone widget renders. */
export const resolveWidgetDisplayTitle = (widget) => {
  const title = resolveWidgetTitleFromProps(widget);
  return title === '' ? CASE_PLAN_WIDGET_FALLBACK_TITLE : title;
};

/**
 * Builds the Delete/Archive confirmation copy for a widget.
 *
 * @param {object} widget - UI widget being removed.
 * @param {'delete' | 'archive'} action - Destructive action the plan status permits.
 * @returns {{ title: string, description: string, modalStatus: 'danger' | 'info' } | null}
 */
export const buildWidgetConfirmationCopy = (widget, action) => {
  const copy = CASE_PLAN_WIDGET_CONFIRMATION_COPY[action];

  if (!copy) {
    return null;
  }

  return {
    title: copy.buildTitle(resolveWidgetDisplayTitle(widget)),
    description: copy.description,
    modalStatus: copy.modalStatus,
  };
};

const isArchivedSection = (section) => section?.status === CASE_PLAN_SECTION_STATUSES.ARCHIVED;

const isDeletedSection = (section) => section?.status === CASE_PLAN_SECTION_STATUSES.DELETED;

/** Active persisted sections — the same set the Active tab and the move API accept. */
const isEligibleWidgetMoveDestination = (section) =>
  Boolean(section?.id) &&
  !isPendingSection(section) &&
  !isArchivedSection(section) &&
  !isDeletedSection(section);

const findSectionIndex = (sections, sectionId) =>
  sections.findIndex((section) => section.id === sectionId);

const withSectionWidgets = (section, widgets) => ({ ...section, widgets });

const replaceSectionWidgets = (sections, sectionIndex, widgets) =>
  sections.map((section, index) =>
    index === sectionIndex ? withSectionWidgets(section, widgets) : section,
  );

const isInBounds = (index, length) => index >= 0 && index < length;

/**
 * Swaps a widget to `toIndex` within its section. Returns the input array unchanged when the
 * move is a no-op (missing section, out of bounds, or the same index) so callers can skip the
 * API the way section reorder does.
 */
export const moveWidgetInSection = (sections, sectionId, fromIndex, toIndex) => {
  const sectionIndex = findSectionIndex(sections, sectionId);

  if (sectionIndex === -1 || fromIndex === toIndex) {
    return sections;
  }

  const widgets = [...(sections[sectionIndex].widgets ?? [])];

  if (!isInBounds(fromIndex, widgets.length) || !isInBounds(toIndex, widgets.length)) {
    return sections;
  }

  const [widget] = widgets.splice(fromIndex, 1);
  widgets.splice(toIndex, 0, widget);

  return replaceSectionWidgets(sections, sectionIndex, widgets);
};

/**
 * Moves a widget to index 0 of another Active persisted section. Same-section, missing ids,
 * and ineligible destinations leave the list unchanged.
 */
export const moveWidgetToSection = (sections, fromSectionId, toSectionId, widgetId) => {
  if (!widgetId || fromSectionId === toSectionId) {
    return sections;
  }

  const fromIndex = findSectionIndex(sections, fromSectionId);
  const toIndex = findSectionIndex(sections, toSectionId);

  if (fromIndex === -1 || toIndex === -1) {
    return sections;
  }

  const destination = sections[toIndex];

  if (!isEligibleWidgetMoveDestination(destination)) {
    return sections;
  }

  const sourceWidgets = sections[fromIndex].widgets ?? [];
  const widget = sourceWidgets.find((entry) => entry.id === widgetId);

  if (!widget) {
    return sections;
  }

  return sections.map((section, index) => {
    if (index === fromIndex) {
      return withSectionWidgets(
        section,
        sourceWidgets.filter((entry) => entry.id !== widgetId),
      );
    }

    if (index === toIndex) {
      return withSectionWidgets(section, [widget, ...(section.widgets ?? [])]);
    }

    return section;
  });
};

/**
 * Puts a widget back at `originalIndex` in its source section after a rejected cross-section
 * move. Other sections are left alone so in-flight edits survive the rollback.
 */
export const revertWidgetMoveToSection = (
  sections,
  fromSectionId,
  toSectionId,
  widgetId,
  originalIndex,
) => {
  const toIndex = findSectionIndex(sections, toSectionId);
  const fromIndex = findSectionIndex(sections, fromSectionId);

  if (toIndex === -1 || fromIndex === -1 || !widgetId) {
    return sections;
  }

  const destinationWidgets = sections[toIndex].widgets ?? [];
  const widget = destinationWidgets.find((entry) => entry.id === widgetId);

  if (!widget) {
    return sections;
  }

  const sourceWidgets = [...(sections[fromIndex].widgets ?? [])];
  const insertAt = Math.min(Math.max(originalIndex, 0), sourceWidgets.length);
  sourceWidgets.splice(insertAt, 0, widget);

  return sections.map((section, index) => {
    if (index === toIndex) {
      return withSectionWidgets(
        section,
        destinationWidgets.filter((entry) => entry.id !== widgetId),
      );
    }

    if (index === fromIndex) {
      return withSectionWidgets(section, sourceWidgets);
    }

    return section;
  });
};

/** Active persisted sections other than the widget's current section. */
export const getWidgetMoveDestinationSections = (sections, currentSectionId) =>
  sections.filter(
    (section) => isEligibleWidgetMoveDestination(section) && section.id !== currentSectionId,
  );

/**
 * Attaches the Move to Different Section kebab item. Widgets without an id are left alone.
 * When the action is unavailable the original array is returned unchanged.
 */
export const withWidgetMoveMenu = (widgets = [], { enabled, onMoveToSection } = {}) => {
  if (!enabled || typeof onMoveToSection !== 'function') {
    return widgets;
  }

  return widgets.map((widget) => {
    if (!widget?.id || isPendingWidget(widget)) {
      return widget;
    }

    return {
      ...widget,
      menuOptions: [
        ...(widget.menuOptions ?? []),
        {
          text: CASE_PLAN_WIDGET_MOVE_TO_SECTION_LABEL,
          onClick: () => onMoveToSection(widget.id),
        },
      ],
    };
  });
};

/**
 * Appends Delete or Archive to each widget's kebab menu. Run after `withWidgetMoveMenu` so Move
 * stays first, matching the shared CasePlanSection Storybook.
 */
export const withWidgetDestructiveMenu = (
  widgets = [],
  { enabled, action, sectionId, onRequestRemoval } = {},
) => {
  if (!enabled || !action || !sectionId || typeof onRequestRemoval !== 'function') {
    return widgets;
  }

  const label =
    action === CASE_PLAN_WIDGET_DESTRUCTIVE_ACTIONS.DELETE
      ? CASE_PLAN_WIDGET_DELETE_LABEL
      : CASE_PLAN_WIDGET_ARCHIVE_LABEL;

  return widgets.map((widget) => {
    if (!widget?.id || isPendingWidget(widget)) {
      return widget;
    }

    return {
      ...widget,
      menuOptions: [
        ...(widget.menuOptions ?? []),
        {
          text: label,
          onClick: () => onRequestRemoval(sectionId, widget, action),
        },
      ],
    };
  });
};

/**
 * Reinserts a widget at its prior position after archive settles or removal fails. The index
 * is clamped to the current list, and a widget already present is never duplicated.
 */
export const reinsertWidgetAtIndex = (sections = [], sectionId, widget, index) => {
  if (!widget) {
    return sections;
  }

  const sectionIndex = findSectionIndex(sections, sectionId);

  if (sectionIndex === -1) {
    return sections;
  }

  const widgets = [...(sections[sectionIndex].widgets ?? [])];
  const existingIndex = widgets.findIndex((entry) => entry.id === widget.id);

  if (existingIndex !== -1) {
    return sections;
  }

  const insertAt = Math.min(Math.max(index, 0), widgets.length);
  widgets.splice(insertAt, 0, widget);

  return replaceSectionWidgets(sections, sectionIndex, widgets);
};
